import React, { useEffect, useRef, useState } from 'react';
import {
  CameraVideo,
  Display,
  ChevronDown,
  ChevronUp,
  CircleFill,
} from 'react-bootstrap-icons';
import clsx from 'clsx';

const MODE_META = {
  camera: { label: 'Camera', Icon: CameraVideo },
  screen: { label: 'Screen', Icon: Display },
};

export default function LiveSelfView({ stream, mode, lastFrameAt }) {
  const videoRef = useRef(null);
  const [collapsed, setCollapsed] = useState(false);
  const [flash, setFlash] = useState(false);

  useEffect(() => {
    const el = videoRef.current;
    if (!el || !stream) return undefined;
    el.srcObject = stream;
    el.play().catch(() => {});
    return () => {
      el.srcObject = null;
    };
  }, [stream, collapsed]);

  // The model receives one frame per second; pulse the badge on each send
  // so it is obvious what is actually being sampled.
  useEffect(() => {
    if (!lastFrameAt) return undefined;
    setFlash(true);
    const timer = setTimeout(() => setFlash(false), 250);
    return () => clearTimeout(timer);
  }, [lastFrameAt]);

  const meta = MODE_META[mode];
  if (!stream || !meta) return null;

  const { label, Icon } = meta;

  return (
    <div
      className={clsx(
        'fixed right-4 top-4 z-[60] overflow-hidden rounded-xl border bg-black shadow-composer transition-colors',
        collapsed ? 'w-[18.2rem]' : 'w-[22.4rem] sm:w-[28rem]',
        flash ? 'border-accent' : 'border-border-subtle'
      )}
    >
      <div className="flex items-center justify-between gap-1 bg-surface-elevated/95 px-2.5 py-1.5">
        <span className="flex min-w-0 items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-text-muted">
          <Icon size={12} className="shrink-0" />
          <span className="truncate">{label}</span>
        </span>
        <span className="flex shrink-0 items-center gap-1.5">
          <CircleFill
            size={7}
            className={clsx(
              'transition-opacity',
              flash ? 'text-accent opacity-100' : 'text-text-muted opacity-30'
            )}
            aria-hidden="true"
          />
          <button
            type="button"
            onClick={() => setCollapsed((prev) => !prev)}
            className="rounded p-0.5 text-text-muted hover:bg-surface-hover hover:text-text-primary"
            aria-label={collapsed ? 'Show preview' : 'Hide preview'}
            title={collapsed ? 'Show preview' : 'Hide preview'}
          >
            {collapsed ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
        </span>
      </div>

      {!collapsed && (
        <div className="relative aspect-video bg-black">
          <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            className={clsx(
              'h-full w-full',
              // Mirror the camera so it reads as a self-view; a shared
              // screen must not be flipped or text becomes unreadable.
              mode === 'camera' ? 'scale-x-[-1] object-cover' : 'object-contain'
            )}
          />
        </div>
      )}
    </div>
  );
}
